import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReactSortable } from "react-sortablejs";
import { FormInput, registerObject } from "go-form";
import { ReactComponent as SortableHandleSvg } from "go-component/images/svg/sortable-handle.svg";
import { LimitComponentVisibility } from "go-core/components/LimitComponentVisibility";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { CustomValidationError } from "go-form/services/types";
import { UpsellApi, UpsellItemApi } from "../../../../../../../../../../../services/Api/Organization/types";
import MenuGridPageItemModal from "../../../../Menus/components/MenuPageItemModal/MenuPageItemModal";

interface Props {
	form: UseFormReturn<UpsellApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<UpsellApi>[]>>;
	isVisible: boolean;
	setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const UpsellFormItemsTable = ({ form, isVisible, setIsVisible }: Props): JSX.Element => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		control,
		setValue,
		register,
		watch,
	} = form;
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const [itemsTableVisible] = useState(fields.length > 0);
	const [showModal, setShowModal] = useState<boolean>(false);
	const isMobile = useWindowSize().isMobile;
	const isSortingRef = useRef(false);

	const handleSave = (item: Record<string, any>) => {
		setShowModal(false);
		append({ item_id: item.context_id, name: item.entity.name, position: watch("items").length });
	};

	useEffect(() => {
		fields.forEach((field: UpsellItemApi, index: number) => {
			registerObject(register, `items.${index}`, ["item_id", "position"]);
			setValue(`items.${index}`, field);
		});
	}, [fields]);

	const updatePositions = (items: UpsellItemApi[]) => {
		if (!isSortingRef.current) return;

		isSortingRef.current = false;
		items.forEach((item, index) => {
			item.position = index;
		});
		replace(items);
	};

	return (
		<LimitComponentVisibility
			classNames="mb-3"
			defaultVisible={itemsTableVisible}
			handleChange={(val) => val === false && setValue("items", [], { shouldDirty: true })}
			id="upsell_items"
			helpText={t("modules.upsell.field.items.help_text")}
			legendText={t("modules.upsell.field.items.title")}
			isVisibleControl={isVisible}
			setIsVisibleControl={setIsVisible}
			unlimitedContent={
				<>
					<div className={`${isMobile ? "table-responsive" : ""}`}>
						<table className="table table-form">
							<thead>
								<tr>
									<th className="action" />
									<th>{t("common.word.item_group")}</th>
									<th className="action" />
								</tr>
							</thead>
							<ReactSortable
								handle=".sortable-handler"
								list={fields}
								tag="tbody"
								onUpdate={() => (isSortingRef.current = true)}
								setList={(newFields) => updatePositions(newFields)}
							>
								{fields.map((item: UpsellItemApi, index: number) => {
									return (
										<tr key={fields[index].key}>
											<td className="action">
												<SortableHandleSvg className="sortable-handler" />
											</td>
											<td>
												<FormInput
													type="hidden"
													errors={errors}
													defaultValue={index}
													name={`items.${index}.position`}
													register={register}
												/>
												<FormInput
													type="hidden"
													register={register}
													errors={errors}
													name={`items.${index}.item_id`}
												/>
												{item.name ?? ""}
											</td>
											<td className="action">
												<RemoveSVG className="icon" onClick={() => remove(index)} />
											</td>
										</tr>
									);
								})}
							</ReactSortable>
						</table>
					</div>
					<Button variant="add" type="button" onClick={() => setShowModal(true)}>
						{`+ ${t("lib:common.action.add")}`}
					</Button>
					{showModal && (
						<MenuGridPageItemModal
							selectedValues={fields.map((field: UpsellItemApi) => {
								return {
									id: field.item_id,
									type: "ITEM",
								};
							})}
							contextTypes={["ITEM"]}
							handleClose={() => setShowModal(false)}
							handleSave={handleSave}
						/>
					)}
				</>
			}
		/>
	);
};
export default UpsellFormItemsTable;
